import { MessageBarType } from '@fluentui/react';
import { IMessageBarMessage, useNotification } from '@h2oai/ui-kit';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { type History } from 'history';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { RoutePaths } from '../pages/Routes';
import { getAccessToken } from '../services/api';
import { useCloudPlatformDiscovery } from '../utils/hooks';
import { formatDefaultDateAndTimeWithSeconds } from '../utils/utils';
import { useRefetchPendingReviews } from './featurestore.hooks';
import {
  CoreService,
  type GenerateTemporaryUploadResponse,
  type GenerateTransformationUploadResponse,
  JobStatus,
  type RawDataLocation,
  type TransformationType,
} from './gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import * as fm from './gen/fetch.pb';
import {
  ContentsTitle,
  type DataSourceFileInfo,
  dataSourceUploadFileFieldMap,
  type FileMd5,
  type NotificationEvent,
  NotificationType,
  notificationTypeNameMap,
} from './models';

interface InitReqWithHeader extends Omit<fm.InitReq, 'headers'> {
  headers: Headers;
}

export type ApiRequest = (
  requestFunction: (req: Record<string, any>, initReq?: fm.InitReq) => Promise<any>,
  req?: Record<string, any>,
  initReq?: fm.InitReq
) => Promise<any>;

export const getInit = (initReq?: fm.InitReq): InitReqWithHeader => {
  const headers: Headers = initReq?.headers ? new Headers(initReq.headers) : new Headers();
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json; charset=utf-8');
  }
  const accessToken = getAccessToken();
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
    headers.set('request-id', window.crypto.randomUUID());
  }
  return initReq ? { ...initReq, headers } : { headers };
};

let featurestoreApiUrl = '';

export const setFeaturestoreApiUrl = (url: string) => {
  featurestoreApiUrl = url;
};

export const fsApi: ApiRequest = (
  requestFunction: (req: Record<string, any>, initReq?: fm.InitReq) => Promise<any>,
  req: Record<string, any> = {}
) => {
  return requestFunction(req, getInit({ pathPrefix: featurestoreApiUrl }));
};

export const useDataSourceUpload = () => {
  const getDataSourcePath = React.useCallback(async (fileMd5s?: FileMd5[]) => {
    if (fileMd5s?.length) {
      const filesWithMd5Checksum = fileMd5s.reduce((o, { file, md5Checksum = '' }) => {
        if (file) o[file.name] = md5Checksum;
        return o;
      }, {} as { [key: string]: string });
      const { fileResponses, directoryUrl = '' }: GenerateTemporaryUploadResponse = await fsApi(
        CoreService.GenerateTemporaryUpload,
        { filesWithMd5Checksum }
      );
      let fileUrl = '';
      if (fileResponses) {
        const fetches: { url: string; init: RequestInit }[] = [];
        fileMd5s.forEach(({ file }) => {
          const fileName = file?.name || '';
          const { presignUrl, headers = {}, url } = fileResponses[fileName] || {};
          if (presignUrl) {
            fetches.push({ url: presignUrl, init: { method: 'PUT', body: file, headers } });
          }
          if (!fileUrl && url) {
            fileUrl = url;
          }
        });
        const results = await Promise.all(fetches.map(async ({ url, init }) => await fetch(url, init)));
        results.forEach((res) => {
          if (!res.ok) {
            throw res;
          }
        });
        return { directoryUrl, fileUrl };
      }
    }
    return {};
  }, []);
  const uploadDataSourceFiles = React.useCallback(
    async (tempDataSourceFileInfo?: DataSourceFileInfo, dataSource: RawDataLocation = {}) => {
      if (tempDataSourceFileInfo) {
        const { fileMd5s, parents = [] } = tempDataSourceFileInfo;
        const { directoryUrl, fileUrl } = await getDataSourcePath(fileMd5s);
        const fieldName = parents[parents.length - 1] as keyof RawDataLocation;
        const pathFieldName = dataSourceUploadFileFieldMap.get(fieldName);
        if (dataSource && pathFieldName) {
          const fieldInfo: { [key: string]: any } = { ...dataSource[fieldName] };
          fieldInfo[pathFieldName] = pathFieldName === 'path' ? fileUrl : directoryUrl;
          dataSource[fieldName] = fieldInfo;
        }
      }
      return dataSource;
    },
    []
  );
  return { getDataSourcePath, uploadDataSourceFiles };
};

export const useTransformationUpload = () => {
  const getFileName = React.useCallback(
    async (file?: File, md5Checksum?: string, transformationType?: TransformationType) => {
      if (file && md5Checksum && transformationType) {
        const {
          filename,
          url = '',
          headers,
        }: GenerateTransformationUploadResponse = await fsApi(CoreService.GenerateTransformationUpload, {
          md5Checksum,
          transformationType,
        });
        if (url) {
          const res = await fetch(url, {
            method: 'PUT',
            body: file,
            headers,
          });
          if (!res.ok) {
            throw res;
          } else {
            return filename;
          }
        }
      }
      return undefined;
    },
    []
  );
  return { getFileName };
};

export type Notification = {
  title: string;
  data: NotificationEvent;
};

export const listenNotifications = (
  featurestoreApiUrl: string,
  onMessage: (notification: { title: string; data: NotificationEvent }) => void,
  requestInit?: { signal?: AbortSignal }
) => {
  const url = new URL(`/api/v1/notifications`, featurestoreApiUrl);
  return fetchEventSource(url.toString(), {
    ...requestInit,
    headers: { Authorization: `Bearer ${getAccessToken()}` },
    onmessage: (event) => {
      const notification = JSON.parse(event.data) as Notification;
      onMessage(notification);
    },
  });
};

const getTitle = (notificationType?: string) => {
  if (notificationType) {
    const titles = notificationType.split('_');
    titles.pop();
    return titles.map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(' ');
  }
  return '';
};

const getNotificationMessage = (notification: NotificationEvent) => {
  const { type, message, eventTime, jobStatus } = notification;
  const titleAndType = notificationTypeNameMap.get(type) || {
    title: getTitle(notification.type),
    messageBarType: MessageBarType.info,
  };
  const headerSecondContent = eventTime ? formatDefaultDateAndTimeWithSeconds(eventTime) : undefined;
  if (type === NotificationType.JOB_FINISHED_EVENT) {
    titleAndType.title = `Job ${jobStatus}`;
    switch (jobStatus) {
      case JobStatus.Cancelled:
        titleAndType.messageBarType = MessageBarType.blocked;
        break;
      case JobStatus.Failed:
      case JobStatus.SubmissionFailed:
        titleAndType.messageBarType = MessageBarType.error;
        break;
      case JobStatus.Success:
        titleAndType.messageBarType = MessageBarType.success;
        break;
      case JobStatus.Failing:
      case JobStatus.Cancelling:
        titleAndType.messageBarType = MessageBarType.warning;
        break;
      default:
        titleAndType.messageBarType = MessageBarType.info;
        break;
    }
  }
  return {
    ...titleAndType,
    message,
    headerSecondContent,
    data: notification,
  };
};

const redirectNotification = (msg: IMessageBarMessage, history: History) => {
  const { draftId, type, resourceId, resourceName }: NotificationEvent = msg.data || {};
  switch (type) {
    case NotificationType.DRAFT_EXTRACT_SCHEMA_FINISHED_EVENT:
      history.push(
        `${RoutePaths.FEATURESTORE_FEATURE_SETS}?tab=${ContentsTitle.ongoingRegistration}&featureSetDraftId=${draftId}`
      );
      break;
    case NotificationType.DRAFT_EXTRACT_SCHEMA_FAILED_EVENT:
      history.push(`${RoutePaths.FEATURESTORE_FEATURE_SETS}?tab=${ContentsTitle.ongoingRegistration}`);
      break;
    case NotificationType.JOB_FINISHED_EVENT:
      history.push(RoutePaths.FEATURESTORE_JOBS);
      break;
    case NotificationType.PERMISSION_PROJECT_REJECTED_EVENT:
    case NotificationType.PERMISSION_PROJECT_REQUEST_EVENT:
      history.push(RoutePaths.FEATURESTORE_PROJECT_PERMISSIONS);
      break;
    case NotificationType.PERMISSION_FEATURE_SET_REQUEST_EVENT:
      history.push(RoutePaths.FEATURESTORE_FEATURE_SETS_PERMISSIONS);
      break;
    case NotificationType.PERMISSION_PROJECT_REVOKED_EVENT:
      history.push(`${RoutePaths.FEATURESTORE_PROJECT_PERMISSIONS}?tab=${ContentsTitle.myPermissions}`);
      break;
    case NotificationType.PERMISSION_FEATURE_SET_GRANTED_EVENT:
      history.push(RoutePaths.FEATURESTORE_MY_FEATURE_SETS);
      break;
    case NotificationType.PERMISSION_FEATURE_SET_REVOKED_EVENT:
      history.push(`${RoutePaths.FEATURESTORE_FEATURE_SETS_PERMISSIONS}?tab=${ContentsTitle.myPermissions}`);
      break;
    case NotificationType.FEATURE_SET_REVIEW_REQUEST_EVENT:
      history.push(RoutePaths.FEATURESTORE_PENDING_REVIEWS);
      break;
    case NotificationType.FEATURE_SET_REVIEW_APPROVED_EVENT:
    case NotificationType.FEATURE_SET_REVIEW_REJECTED_EVENT:
      history.push(RoutePaths.FEATURESTORE_PENDING_FEATURE_SETS);
      break;
    case NotificationType.PERMISSION_PROJECT_GRANTED_EVENT:
    case NotificationType.PROJECT_UPDATED_EVENT:
      history.push(`${RoutePaths.FEATURESTORE_PROJECT}/${resourceId}?name=${resourceName}`);
      break;
    case NotificationType.FEATURE_SET_UPDATED_EVENT:
      history.push(`${RoutePaths.FEATURESTORE_FEATURE_SET}/${resourceId}`);
      break;
  }
};

export const useHandleNotifications = () => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const featurestoreApiUrl = cloudPlatformDiscovery?.featurestoreApiUrl;
  const { addNotification, readNotification } = useNotification();
  const refetchPendingReviews = useRefetchPendingReviews();
  const history = useHistory();
  React.useEffect(() => {
    if (!featurestoreApiUrl) return undefined;

    const abortController = new AbortController();
    listenNotifications(
      featurestoreApiUrl,
      (notification) => {
        if (notification.title) {
          const nType = notification.data.type;
          if (
            nType === NotificationType.FEATURE_SET_REVIEW_REQUEST_EVENT ||
            nType === NotificationType.FEATURE_SET_REVIEW_APPROVED_EVENT ||
            nType === NotificationType.FEATURE_SET_REVIEW_REJECTED_EVENT
          ) {
            refetchPendingReviews();
          }
          const notificationMessage = getNotificationMessage(notification.data);
          addNotification({
            ...notificationMessage,
            onClick: (_e, msg) => {
              readNotification(msg.id);
              redirectNotification(msg, history);
            },
          });
        }
      },
      { signal: abortController.signal }
    ).catch((error) => {
      if (!abortController.signal.aborted) {
        console.error('Error listening to notifications:', error);
      }
    });
    return () => {
      abortController.abort();
    };
  }, [featurestoreApiUrl]);
};
